import {
  type Cell,
  type ColumnDef,
  type Row,
  type Table,
} from "@tanstack/react-table";

import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { type ColumnGroup, type ExpandableRowConfig } from "../../types";

import { MobileTableCell } from "./shared/MobileTableCell";
import { MobileTableRowHeader } from "./MobileTableRowHeader";

type MobileTableRowProps<TData, TValue = unknown> = {
  row: Row<TData>;
  table: Table<TData>;
  columns: ColumnDef<TData, TValue>[];
  columnGroups?: ColumnGroup<TData, TValue>[];
  cellClassName?: string;
  wrapperClassName?: string;
  hasRowClick?: boolean;
  enableRowSelection: boolean;
  selectOnRowClick: boolean;
  canSelect: boolean;
  isSelected: boolean;
  onRowClick?: (row: TData) => void;
  expandableConfig?: ExpandableRowConfig<TData>;
  handleExpandRow: (rowId: string) => void;
  hideUnselectableCheckboxes?: boolean;
  dimUnselectableRows?: boolean;
};

export function MobileTableRow<TData, TValue = unknown>({
  row,
  table,
  columnGroups,
  cellClassName,
  wrapperClassName,
  hasRowClick,
  enableRowSelection,
  selectOnRowClick,
  canSelect,
  isSelected,
  onRowClick,
  expandableConfig,
  handleExpandRow,
  hideUnselectableCheckboxes = false,
  dimUnselectableRows = true,
}: MobileTableRowProps<TData, TValue>) {
  const cells = row.getVisibleCells();
  const isExpandable =
    !!expandableConfig && expandableConfig.isExpandable(row.original);

  const getHeaderCells = () => {
    const headerCells = cells.filter(
      (cell: Cell<TData, unknown>) =>
        cell.column.columnDef.isMobileHeader ||
        cell.column.columnDef.isMobileSelector,
    );

    return headerCells.length ? headerCells : [cells[0]];
  };

  const headerCells = getHeaderCells();
  const actionCell = cells.find(
    (cell: Cell<TData, unknown>) => cell.column.columnDef.isMobileAction,
  );

  const checkIsMobileHiddenOnExpanded = (cell: Cell<TData, unknown>) => {
    const hasParentId =
      expandableConfig && expandableConfig.getParentId?.(row.original);

    if (!hasParentId) return true;

    if (hasParentId) return !cell.column.columnDef.isMobileHiddenOnExpanded;
  };
  const remainingCells = cells.filter(
    (cell: Cell<TData, unknown>) =>
      !headerCells.includes(cell) &&
      !cell.column.columnDef.isMobileAction &&
      !cell.column.columnDef.isMobileSelector &&
      !cell.column.columnDef.isMobileHidden &&
      checkIsMobileHiddenOnExpanded(cell) &&
      cell.column.columnDef.header &&
      cell.column.id !== "expandable",
  );

  const id = expandableConfig?.getRowId(row.original);

  const columnGroupMap = new Map<string, string>();

  if (columnGroups) {
    for (const group of columnGroups) {
      for (const col of group.columns) {
        const colId =
          col.id ??
          ("accessorKey" in col && typeof col.accessorKey === "string"
            ? col.accessorKey.replace(/\./g, "_")
            : undefined);

        if (colId !== undefined) columnGroupMap.set(colId, group.label);
      }
    }
  }

  type CellGroup = { label: string; cells: Cell<TData, unknown>[] };
  const groupedRemainingCells: CellGroup[] = [];

  if (columnGroups) {
    let currentGroup: CellGroup | null = null;
    for (const cell of remainingCells) {
      const label = columnGroupMap.get(cell.column.id) ?? "";

      if (!currentGroup || currentGroup.label !== label) {
        currentGroup = { label, cells: [] };
        groupedRemainingCells.push(currentGroup);
      }
      currentGroup.cells.push(cell);
    }
  }

  return (
    <div
      className={cnTwMerge(
        "border border-border/primary rounded-containerCorner overflow-hidden",
        {
          "cursor-pointer hover:bg-background/secondary":
            hasRowClick ||
            (enableRowSelection && selectOnRowClick && canSelect),
        },
        isSelected && "bg-background/secondary/60",
        !canSelect && enableRowSelection && dimUnselectableRows && "opacity-50",
        wrapperClassName,
      )}
      data-state={isSelected ? "selected" : ""}
      data-testid={`mobile-row-${row.id}`}
      onClick={() => !isExpandable && onRowClick?.(row.original)}
    >
      <MobileTableRowHeader
        actionCell={actionCell}
        canSelect={canSelect}
        cellClassName={cellClassName}
        enableRowSelection={enableRowSelection}
        headerCells={headerCells}
        hideUnselectableCheckboxes={hideUnselectableCheckboxes}
        isSelected={isSelected}
        row={row}
      />

      {columnGroups
        ? groupedRemainingCells.map((group, i) => (
            <div
              className={
                group.label ? "border-l-2 border-l-border/brand" : undefined
              }
              // due to column grouping, not all table headers will have a label, so we need to use the index as a key
              // eslint-disable-next-line react/no-array-index-key
              key={`group-${group.label}-${i}`}
            >
              {group.label ? (
                <div className="border-t border-border/primary bg-background/secondary px-4 py-2 body-small-semibold text-text/primary">
                  {group.label}
                </div>
              ) : null}
              {group.cells.map((cell: Cell<TData, unknown>) => (
                <MobileTableCell
                  cell={cell}
                  cellClassName={cellClassName}
                  key={cell.id}
                  rowId={row.id}
                  table={table}
                />
              ))}
            </div>
          ))
        : remainingCells.map((cell: Cell<TData, unknown>) => (
            <MobileTableCell
              cell={cell}
              cellClassName={cellClassName}
              key={cell.id}
              rowId={row.id}
              table={table}
            />
          ))}

      {isExpandable ? (
        <div className="border-t border-border/primary bg-background/primary p-4">
          <div
            className="flex justify-center text-text/pending"
            onClick={(e) => {
              e.stopPropagation();
              id && handleExpandRow(id);
            }}
          >
            {m.btn_see_breakdown()}
          </div>
        </div>
      ) : null}
    </div>
  );
}
